import { useState, useEffect, useMemo } from 'react';
import { 
  Button, 
  Group, 
  Select, 
  Stack, 
  Text, 
  Alert, 
  Loader, 
  Center, 
  Tabs, 
  Card,
  Radio,
  TextInput,
  Box,
  MultiSelect
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconSearch, IconX } from '@tabler/icons-react';
import { SortableTable, ColumnDef } from '../SortableTable';
import classes from './ContractDefaultUsersManagement.module.css';
import { 
  getAddressSystems, 
  getContractStatuses, 
  getContractDefaultUsersData, 
  saveContractDefaultUsers 
} from '../../services/ContractDefaultUsersService';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";
import { 
  ContractDefaultUsersData, 
  UserInfo, 
  ContractDefaultUsersRequest, 
  ContractStatusDropdownItem, 
  AddressSystemDropdownItem, 
  DefaultUserRole,
  defaultUserRoleLabels,
  ContractUserAssignments
} from '../../types/ContractDefaultUsers';

export default function ContractDefaultUsersManagement() {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 318, addressSystemId: addrIdFromStore || 0, autoLoad: true });

  const [addressSystems, setAddressSystems] = useState<AddressSystemDropdownItem[]>([]);
  const [contractStatuses, setContractStatuses] = useState<ContractStatusDropdownItem[]>([]);
  const [selectedAddressSystemId, setSelectedAddressSystemId] = useState<string | null>(null);
  const [selectedContractStatusId, setSelectedContractStatusId] = useState<string | null>(null);
  const [selectedContractIds, setSelectedContractIds] = useState<string[]>(['allContracts']);
  const [data, setData] = useState<ContractDefaultUsersData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [originalData, setOriginalData] = useState<ContractDefaultUsersData | null>(null);
  const [activeTab, setActiveTab] = useState<string>(DefaultUserRole.Foreman);

  // Column filter states for each tab
  const [foremanNameFilter, setForemanNameFilter] = useState<string[]>([]);
  const [foremanOrganisationFilter, setForemanOrganisationFilter] = useState<string[]>([]);
  const [foremanRoleFilter, setForemanRoleFilter] = useState<string[]>([]);
  
  const [contractorForemanNameFilter, setContractorForemanNameFilter] = useState<string[]>([]);
  const [contractorForemanOrganisationFilter, setContractorForemanOrganisationFilter] = useState<string[]>([]);
  const [contractorForemanRoleFilter, setContractorForemanRoleFilter] = useState<string[]>([]);
  
  const [officeNameFilter, setOfficeNameFilter] = useState<string[]>([]);
  const [officeOrganisationFilter, setOfficeOrganisationFilter] = useState<string[]>([]);
  const [officeRoleFilter, setOfficeRoleFilter] = useState<string[]>([]);

  // Filter users based on search term - moved from renderUserTable to fix ESLint error
  const filteredForemanUsers = useMemo(() => {
    if (!data?.foremanUsers) return [];
    
    let filtered = data.foremanUsers;
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchLower) ||
        user.name.toLowerCase().includes(searchLower) ||
        user.companyName.toLowerCase().includes(searchLower) ||
        user.roleName.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply column filters
    if (foremanNameFilter.length > 0) {
      filtered = filtered.filter(user => foremanNameFilter.includes(user.name));
    }
    if (foremanOrganisationFilter.length > 0) {
      filtered = filtered.filter(user => foremanOrganisationFilter.includes(user.companyName));
    }
    if (foremanRoleFilter.length > 0) {
      filtered = filtered.filter(user => foremanRoleFilter.includes(user.roleName));
    }
    
    return filtered;
  }, [data?.foremanUsers, searchTerm, foremanNameFilter, foremanOrganisationFilter, foremanRoleFilter]);

  const filteredContractorForemanUsers = useMemo(() => {
    if (!data?.contractorForemanUsers) return [];
    
    let filtered = data.contractorForemanUsers;
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchLower) ||
        user.name.toLowerCase().includes(searchLower) ||
        user.companyName.toLowerCase().includes(searchLower) ||
        user.roleName.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply column filters
    if (contractorForemanNameFilter.length > 0) {
      filtered = filtered.filter(user => contractorForemanNameFilter.includes(user.name));
    }
    if (contractorForemanOrganisationFilter.length > 0) {
      filtered = filtered.filter(user => contractorForemanOrganisationFilter.includes(user.companyName));
    }
    if (contractorForemanRoleFilter.length > 0) {
      filtered = filtered.filter(user => contractorForemanRoleFilter.includes(user.roleName));
    }
    
    return filtered;
  }, [data?.contractorForemanUsers, searchTerm, contractorForemanNameFilter, contractorForemanOrganisationFilter, contractorForemanRoleFilter]);

  const filteredOfficeUsers = useMemo(() => {
    if (!data?.officeUsers) return [];
    
    let filtered = data.officeUsers;
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchLower) ||
        user.name.toLowerCase().includes(searchLower) ||
        user.companyName.toLowerCase().includes(searchLower) ||
        user.roleName.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply column filters
    if (officeNameFilter.length > 0) {
      filtered = filtered.filter(user => officeNameFilter.includes(user.name));
    }
    if (officeOrganisationFilter.length > 0) {
      filtered = filtered.filter(user => officeOrganisationFilter.includes(user.companyName));
    }
    if (officeRoleFilter.length > 0) {
      filtered = filtered.filter(user => officeRoleFilter.includes(user.roleName));
    }
    
    return filtered;
  }, [data?.officeUsers, searchTerm, officeNameFilter, officeOrganisationFilter, officeRoleFilter]);

  // Filter contracts based on selection
  const displayedContracts = useMemo(() => {
    if (!data?.contracts) return [];
    if (selectedContractIds.includes('allContracts')) {
      return data.contracts;
    }
    return data.contracts.filter(contract => selectedContractIds.includes(contract.id.toString()));
  }, [data?.contracts, selectedContractIds]);

  // Load address systems on mount
  useEffect(() => {
    const loadAddressSystems = async () => {
      try {
        const systems = await getAddressSystems();
        setAddressSystems(systems);
      } catch (error) {
        notifications.show({
          title: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ERROR_TITLE") || "Error",
          message: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_LOAD_ADDRESS_SYSTEMS_FAILED") || "Failed to load address systems",
          color: 'red',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
          className: 'notification-error'
        });
      }
    };
    
    loadAddressSystems();
  }, []);

  // Load contract statuses when address system changes
  useEffect(() => {
    const loadContractStatuses = async () => {
      if (!selectedAddressSystemId) return;

      try {
        const statuses = await getContractStatuses(parseInt(selectedAddressSystemId));
        setContractStatuses(statuses);
      } catch (error) {
        notifications.show({
          title: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ERROR_TITLE") || "Error",
          message: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_LOAD_STATUSES_FAILED") || "Failed to load contract statuses",
          color: 'red',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
          className: 'notification-error'
        });
      }
    };

    if (selectedAddressSystemId) {
      loadContractStatuses();
    } else {
      setContractStatuses([]);
      setSelectedContractStatusId(null);
    }
  }, [selectedAddressSystemId]);

  // Load data when both address system and status are selected
  useEffect(() => {
    const loadData = async () => {
      if (!selectedAddressSystemId || !selectedContractStatusId) return;

      setLoading(true);
      try {
        const result = await getContractDefaultUsersData(
          parseInt(selectedAddressSystemId),
          parseInt(selectedContractStatusId)
        );
        setData(result);
        setOriginalData(JSON.parse(JSON.stringify(result))); // Deep copy for reset functionality
      } catch (error) {
        notifications.show({
          title: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ERROR_TITLE") || "Error",
          message: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_LOAD_DATA_FAILED") || "Failed to load contract default users data",
          color: 'red',
          withBorder: true,
          withCloseButton: true,
          autoClose: 5000,
          className: 'notification-error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (selectedAddressSystemId && selectedContractStatusId) {
      loadData();
    } else {
      setData(null);
      setSelectedContractIds(['allContracts']); // Reset contract selection when clearing data
    }
  }, [selectedAddressSystemId, selectedContractStatusId]);

  // Reset contract selection when contracts change
  useEffect(() => {
    if (data?.contracts && selectedContractIds.includes('allContracts')) {
      setSelectedContractIds(['allContracts', ...data.contracts.map(c => c.id.toString())]);
    }
  }, [data?.contracts, selectedContractIds]);

  const handleDefaultChange = (userId: number, contractId: number, isDefault: boolean, roleType: DefaultUserRole) => {
    if (!data) return;

    setData(prevData => {
      if (!prevData) return prevData;

      const updatedData = { ...prevData };
      let usersArray: UserInfo[] = [];
      
      switch (roleType) {
        case DefaultUserRole.Foreman:
          usersArray = [...prevData.foremanUsers];
          break;
        case DefaultUserRole.ContractorForeman:
          usersArray = [...prevData.contractorForemanUsers];
          break;
        case DefaultUserRole.OfficeUser:
          usersArray = [...prevData.officeUsers];
          break;
      }

      // Clear any existing default for this contract
      usersArray.forEach(user => {
        if (user.defaultAssignments[contractId]) {
          switch (roleType) {
            case DefaultUserRole.Foreman:
              user.defaultAssignments[contractId].isForeman = false;
              break;
            case DefaultUserRole.ContractorForeman:
              user.defaultAssignments[contractId].isContractorForeman = false;
              break;
            case DefaultUserRole.OfficeUser:
              user.defaultAssignments[contractId].isOfficeUser = false;
              break;
          }
        }
      });

      // Set the new default if isDefault is true
      if (isDefault) {
        const userIndex = usersArray.findIndex(user => user.id === userId);
        if (userIndex !== -1) {
          if (!usersArray[userIndex].defaultAssignments[contractId]) {
            usersArray[userIndex].defaultAssignments[contractId] = {
              isForeman: false,
              isContractorForeman: false,
              isOfficeUser: false
            };
          }
          
          switch (roleType) {
            case DefaultUserRole.Foreman:
              usersArray[userIndex].defaultAssignments[contractId].isForeman = true;
              break;
            case DefaultUserRole.ContractorForeman:
              usersArray[userIndex].defaultAssignments[contractId].isContractorForeman = true;
              break;
            case DefaultUserRole.OfficeUser:
              usersArray[userIndex].defaultAssignments[contractId].isOfficeUser = true;
              break;
          }
        }
      }

      // Update the appropriate users array in the data
      switch (roleType) {
        case DefaultUserRole.Foreman:
          updatedData.foremanUsers = usersArray;
          break;
        case DefaultUserRole.ContractorForeman:
          updatedData.contractorForemanUsers = usersArray;
          break;
        case DefaultUserRole.OfficeUser:
          updatedData.officeUsers = usersArray;
          break;
      }

      return updatedData;
    });
  };

  const handleSave = async () => {
    if (!selectedAddressSystemId || !selectedContractStatusId || !data) return;

    setSaving(true);
    try {
      // Build the request structure based on the current API
      const userAssignments: Record<number, ContractUserAssignments> = {};

      // Initialize with empty assignments for all contracts
      data.contracts.forEach(contract => {
        userAssignments[contract.id] = {
          foremanUserId: undefined,
          contractorForemanUserId: undefined,
          officeUserId: undefined
        };
      });

      // Set foreman users
      data.foremanUsers.forEach(user => {
        Object.entries(user.defaultAssignments).forEach(([contractIdStr, assignment]) => {
          const contractId = parseInt(contractIdStr);
          if (assignment.isForeman && userAssignments[contractId]) {
            userAssignments[contractId].foremanUserId = user.id;
          }
        });
      });

      // Set contractor foreman users
      data.contractorForemanUsers.forEach(user => {
        Object.entries(user.defaultAssignments).forEach(([contractIdStr, assignment]) => {
          const contractId = parseInt(contractIdStr);
          if (assignment.isContractorForeman && userAssignments[contractId]) {
            userAssignments[contractId].contractorForemanUserId = user.id;
          }
        });
      });

      // Set office users
      data.officeUsers.forEach(user => {
        Object.entries(user.defaultAssignments).forEach(([contractIdStr, assignment]) => {
          const contractId = parseInt(contractIdStr);
          if (assignment.isOfficeUser && userAssignments[contractId]) {
            userAssignments[contractId].officeUserId = user.id;
          }
        });
      });

      const request: ContractDefaultUsersRequest = {
        addressSystemId: parseInt(selectedAddressSystemId),
        contractStatusId: parseInt(selectedContractStatusId),
        userAssignments
      };

      await saveContractDefaultUsers(request);

      notifications.show({
        title: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_SUCCESS_TITLE") || "Success",
        message: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_SAVED") || "Contract default users saved successfully",
        color: 'green',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-success'
      });
    } catch (error) {
      notifications.show({
        title: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ERROR_TITLE") || "Error",
        message: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_SAVE_FAILED") || "Failed to save contract default users",
        color: 'red',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalData) {
      setData(JSON.parse(JSON.stringify(originalData))); // Deep copy to reset data
      notifications.show({
        title: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_CHANGES_RESET_TITLE") || "Changes Reset",
        message: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_CHANGES_RESET_MESSAGE") || "All changes have been reset to original values",
        color: 'blue',
        withBorder: true,
        withCloseButton: true,
        autoClose: 5000,
        className: 'notification-info'
      });
    }
  };

  const renderUserTable = (users: UserInfo[], roleType: DefaultUserRole) => {
    if (!data) return null;

    if (users.length === 0) {
      return (
        <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
          {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_NONE_FOR_ROLE") || "No users found for this role."}
        </Alert>
      );
    }

    // Get the appropriate filter functions based on role type
    const getFilterStates = () => {
      switch (roleType) {
        case DefaultUserRole.Foreman:
          return {
            nameFilter: foremanNameFilter,
            setNameFilter: setForemanNameFilter,
            organisationFilter: foremanOrganisationFilter,
            setOrganisationFilter: setForemanOrganisationFilter,
            roleFilter: foremanRoleFilter,
            setRoleFilter: setForemanRoleFilter,
            allUsers: data?.foremanUsers || []
          };
        case DefaultUserRole.ContractorForeman:
          return {
            nameFilter: contractorForemanNameFilter,
            setNameFilter: setContractorForemanNameFilter,
            organisationFilter: contractorForemanOrganisationFilter,
            setOrganisationFilter: setContractorForemanOrganisationFilter,
            roleFilter: contractorForemanRoleFilter,
            setRoleFilter: setContractorForemanRoleFilter,
            allUsers: data?.contractorForemanUsers || []
          };
        case DefaultUserRole.OfficeUser:
          return {
            nameFilter: officeNameFilter,
            setNameFilter: setOfficeNameFilter,
            organisationFilter: officeOrganisationFilter,
            setOrganisationFilter: setOfficeOrganisationFilter,
            roleFilter: officeRoleFilter,
            setRoleFilter: setOfficeRoleFilter,
            allUsers: data?.officeUsers || []
          };
        default:
          return {
            nameFilter: [],
            setNameFilter: () => {},
            organisationFilter: [],
            setOrganisationFilter: () => {},
            roleFilter: [],
            setRoleFilter: () => {},
            allUsers: []
          };
      }
    };

    const filterStates = getFilterStates();

    // Define base columns
    const columns: ColumnDef<UserInfo>[] = [
      // {
      //   header: 'Username',
      //   accessor: 'username',
      //   sortable: true,
      //   width: 120
      // },
      {
        header: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_NAME") || "Name",
        accessor: 'name',
        sortable: true,
        width: 200,
        render: (user) => (
          <span title={`Username: ${user.username}`}>
            {user.name || '-'}
          </span>
        ),
        filter: {
          type: 'multiselect',
          value: filterStates.nameFilter,
          onChange: filterStates.setNameFilter,
          options: () => Array.from(new Set(filterStates.allUsers.map(u => u.name)).values())
            .filter(Boolean)
            .map(name => ({ value: name, label: name })),
          placeholder: getTranslation("PLACEHOLDER_CONTRACT_DEFAULT_USERS_FILTER_NAMES") || "Filter names..."
        }
      },
      {
        header: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ORGANISATION") || "Organisation",
        accessor: 'companyName',
        sortable: true,
        width: 200,
        render: (user) => user.companyName || '-',
        filter: {
          type: 'multiselect',
          value: filterStates.organisationFilter,
          onChange: filterStates.setOrganisationFilter,
          options: () => Array.from(new Set(filterStates.allUsers.map(u => u.companyName)).values())
            .filter(Boolean)
            .map(name => ({ value: name, label: name })),
          placeholder: getTranslation("PLACEHOLDER_CONTRACT_DEFAULT_USERS_FILTER_ORGS") || "Filter organisations..."
        }
      },
      {
        header: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ROLE") || "Role",
        accessor: 'roleName',
        sortable: true,
        width: 250,
        render: (user) => user.roleName || '-',
        filter: {
          type: 'multiselect',
          value: filterStates.roleFilter,
          onChange: filterStates.setRoleFilter,
          options: () => Array.from(new Set(filterStates.allUsers.map(u => u.roleName)).values())
            .filter(Boolean)
            .map(name => ({ value: name, label: name })),
          placeholder: getTranslation("PLACEHOLDER_CONTRACT_DEFAULT_USERS_FILTER_ROLES") || "Filter roles..."
        }
      }
    ];

    // Add contract columns dynamically
    displayedContracts.forEach(contract => {
      columns.push({
        header: (
          <div className={classes.contractHeader}>
            {contract.name}
          </div>
        ),
        accessor: `contract_${contract.id}`,
        sortable: false,
        width: 250,
        render: (user) => {
          // Check if user has access to this contract
          const hasAccess = user.contractPermissions[contract.id];
          const hasRoleAccess = hasAccess && (
            (roleType === DefaultUserRole.Foreman && hasAccess.isForeman) ||
            (roleType === DefaultUserRole.ContractorForeman && hasAccess.isContractorForeman) ||
            (roleType === DefaultUserRole.OfficeUser && hasAccess.isOfficeUser)
          );

          if (!hasRoleAccess) {
            return (
              <Box className={classes.noAccessText}>
                <IconX size={16} color="red" />
              </Box>
            );
          }

          const isSelected = user.defaultAssignments[contract.id] && (
            (roleType === DefaultUserRole.Foreman && user.defaultAssignments[contract.id].isForeman) ||
            (roleType === DefaultUserRole.ContractorForeman && user.defaultAssignments[contract.id].isContractorForeman) ||
            (roleType === DefaultUserRole.OfficeUser && user.defaultAssignments[contract.id].isOfficeUser)
          );

          return (
            <Box className={classes.radioContainer}>
              <Radio
                checked={!!isSelected}
                onChange={(event) => 
                  handleDefaultChange(user.id, contract.id, event.currentTarget.checked, roleType)
                }
                name={`${roleType}_${contract.id}`}
                title={getTranslation("TITLE_SET_AS_DEFAULT_USER") || "Set as default user"}
              />
            </Box>
          );
        }
      });
    });

    return (
      <Box className={classes.tableContainer}>
        <SortableTable
          data={users}
          columns={columns}
          rowKey={(user) => user.id.toString()}
          pageSize={50}
          showPagination={true}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>
    );
  };

  return (
    <>
    {/* <div className={classes.mainContainer}> */}
      <Group justify="space-between" align="end" mb='xs'>
        <Select
          label={getTranslation("LABEL_ADDRESS_SYSTEM") || "Address System"}
          placeholder={getTranslation("PLACEHOLDER_SELECT_ADDRESS_SYSTEM") || "Select address system"}
          value={selectedAddressSystemId}
          onChange={(value) => {
            setSelectedAddressSystemId(value);
            setSelectedContractStatusId(null);
            setData(null);
            setSelectedContractIds(['allContracts']); // Reset contract selection
          }}
          data={addressSystems.map(system => ({
            value: system.id.toString().trim(),
            label: system.name.trim(),
          }))}
          style={{ flex: 1, minWidth: '200px' }}
          searchable
          spellCheck={false}
        />
        
        <Select
          label={getTranslation("LABEL_STATUS") || "Status"}
          placeholder={getTranslation("PLACEHOLDER_SELECT_CONTRACT_STATUS") || "Select contract status"}
          value={selectedContractStatusId}
          onChange={setSelectedContractStatusId}
          data={contractStatuses.map(status => ({
            value: status.id.toString(),
            label: status.name
          }))}
          style={{ flex: 1, minWidth: '200px', marginLeft: '16px' }}
          disabled={!selectedAddressSystemId}
          searchable
          spellCheck={false}
        />

        <TextInput
          label={getTranslation("LABEL_SEARCH") || "Search"}
          placeholder={getTranslation("PLACEHOLDER_SEARCH_USERS") || "Search users..."}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
          leftSection={<IconSearch size={18} color="#009DF0" />}
          style={{ flex: 1, minWidth: '200px', marginLeft: '16px' }}
          disabled={!data}
          spellCheck={false}
        />
      </Group>

      <Group justify="flex-start" align="end" mb="md">
        <MultiSelect
          label={getTranslation("LABEL_CONTRACTS") || "Contracts"}
          placeholder={selectedContractIds.length === 0 ? (getTranslation("PLACEHOLDER_CONTRACT_DEFAULT_USERS_SELECT_CONTRACTS") || "Select contracts") : ""}
          value={selectedContractIds.includes('allContracts')
            ? data?.contracts.map(c => c.id.toString()) || []
            : selectedContractIds
          }
          onChange={(value) => {
            if (value.includes('allContracts')) {
              setSelectedContractIds(['allContracts', ...(data?.contracts.map(c => c.id.toString()) || [])]);
            } else {
              if (value.length === (data?.contracts.length || 0)) {
                setSelectedContractIds(['allContracts', ...value]);
              } else {
                setSelectedContractIds(value.filter(id => id !== 'allContracts'));
              }
            }
          }}
          data={[
            ...(selectedContractIds.includes('allContracts') ? [] : [{ value: 'allContracts', label: getTranslation("LABEL_CONTRACT_DEFAULT_USERS_ALL_CONTRACTS") || "All Contracts" }]),
            ...(data?.contracts.map(contract => ({
              value: contract.id.toString(),
              label: contract.name
            })) || [])
          ]}
          style={{ minWidth: '600px', flex: 1 }}
          styles={{
            input: {
              minHeight: '72px',
              maxHeight: '72px',
              overflow: 'auto'
            },
            pillsList: {
              flexWrap: 'wrap',
              overflow: 'auto',
              maxHeight: '48px',
              position: 'relative',
              left: '0px',
              paddingLeft: '8px',
              paddingRight: '8px'
            }
          }}
          searchable
          hidePickedOptions={false}
          clearable
          withCheckIcon
          disabled={!selectedAddressSystemId || !selectedContractStatusId || !data}
        />
      </Group>

      {!selectedAddressSystemId && (
        <Alert icon={<IconAlertCircle size={18} />} color="blue">
          {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_SELECT_ADDRESS_SYSTEM") || "Please select an address system to continue."}
        </Alert>
      )}

      {selectedAddressSystemId && !selectedContractStatusId && (
        <Alert icon={<IconAlertCircle size={18} />} color="blue">
          {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_SELECT_STATUS") || "Please select a contract status to continue."}
        </Alert>
      )}

      {selectedAddressSystemId && selectedContractStatusId && (
        <Box className={classes.container}>
          {loading ? (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          ) : data ? (
            <Card className={classes.cardContainer}>
              <Stack gap="md" className={classes.stackContainer}>
                {/* <Text size="lg" fw={500}>Contract Default Users Management</Text> */}

                <Alert color="blue">
                  <Text size="sm">
                    {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_HELP_TEXT") || "Select default users for each role type across contracts. Only users who have access to contracts with the specific role are shown. Use radio buttons to designate which user should be the default for each contract and role combination."}
                  </Text>
                </Alert>

                <Tabs value={activeTab} onChange={(value) => setActiveTab(value || DefaultUserRole.Foreman)} className={classes.tabsContainer}>
                  <Tabs.List>
                    <Tabs.Tab value={DefaultUserRole.Foreman}>
                      {defaultUserRoleLabels[DefaultUserRole.Foreman]} ({filteredForemanUsers.length})
                    </Tabs.Tab>
                    <Tabs.Tab value={DefaultUserRole.ContractorForeman}>
                      {defaultUserRoleLabels[DefaultUserRole.ContractorForeman]} ({filteredContractorForemanUsers.length})
                    </Tabs.Tab>
                    <Tabs.Tab value={DefaultUserRole.OfficeUser}>
                      {defaultUserRoleLabels[DefaultUserRole.OfficeUser]} ({filteredOfficeUsers.length})
                    </Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value={DefaultUserRole.Foreman} pt="md" className={classes.tabPanel}>
                    {renderUserTable(filteredForemanUsers, DefaultUserRole.Foreman)}
                  </Tabs.Panel>

                  <Tabs.Panel value={DefaultUserRole.ContractorForeman} pt="md" className={classes.tabPanel}>
                    {renderUserTable(filteredContractorForemanUsers, DefaultUserRole.ContractorForeman)}
                  </Tabs.Panel>

                  <Tabs.Panel value={DefaultUserRole.OfficeUser} pt="md" className={classes.tabPanel}>
                    {renderUserTable(filteredOfficeUsers, DefaultUserRole.OfficeUser)}
                  </Tabs.Panel>
                </Tabs>

                <Group justify="flex-start" gap="md">
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    disabled={!originalData}
                  >
                    {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_RESET_ALL") || "Reset All Changes"}
                  </Button>
                  <Button
                    onClick={handleSave}
                    loading={saving}
                    disabled={data.contracts.length === 0}
                  >
                    {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_SAVE") || "Save"}
                  </Button>
                </Group>
              </Stack>
            </Card>
          ) : (
            <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
              {getTranslation("LABEL_CONTRACT_DEFAULT_USERS_NO_DATA") || "No data available for the selected criteria."}
            </Alert>
          )}
        </Box>
      )}
    {/* </div> */}
    </>
  );
}